import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faEllipsisH,
  faEye,
  faToggleOn,
  faToggleOff,
  faTrashAlt,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import {
  Button,
  Card,
  Table,
  Pagination,
  ButtonGroup,
  Dropdown,
} from "@themesberg/react-bootstrap";
import { Nav, Col, Row, Form, InputGroup } from "@themesberg/react-bootstrap";
import axios from "axios";
import BASE_URL from "../config";


export const UserlistTable = () => {
  const [users, setUsers] = useState([]);
  const [loggedInUser, setLoggedInUser] = useState(null); // Store logged-in user details
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const usersPerPage = 5;

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;

  // Filter users based on the search query
  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  useEffect(() => {
    getAllUsers();
    fetchLoggedInUser(); // Get logged-in user's details from localStorage
  }, []);

  const fetchLoggedInUser = () => {
    const user = JSON.parse(localStorage.getItem("authToken"));
    if (user) {
      setLoggedInUser(user); // Store the logged-in user
    } else {
      console.error("User data not found in localStorage.");
    }
  };

  async function getAllUsers() {
    try {
      const response = await axios.get(`${BASE_URL}/api.php?type=users`);
      const data = response.data;
      // console.log(data);
      
      setUsers(Array.isArray(data) ? data : []);
      setLoading(false);
    } catch (error) {
      console.log("Something went wrong:", error);
      setUsers([]);
      setLoading(false);
    }
  }

  const toggleStatus = async (userId, newStatus) => {
    try {
      await axios.put(`${BASE_URL}/api.php`, {
        type: "updateUserStatus",
        id: userId,
        status: newStatus,
      });
      getAllUsers(); // Refresh user list
    } catch (error) {
      console.error("Error updating user status:", error);
    }
  };

  const deleteUser = async (userId) => {
    try {
      await axios.delete(`${BASE_URL}/api.php`, {
        data: {
          type: "users",
          id: userId,
        },
      });
      getAllUsers(); // Refresh user list after deletion
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  if (loading || !loggedInUser) {
    return <p>Loading...</p>;
  }

  return (
    <Card border="light" className="table-wrapper table-responsive shadow-sm">
      <Card.Body>
        <div className="table-settings mb-4">
          <Row className="justify-content-between align-items-center">
            <Col xs={8} md={3}>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by name or email"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>
            </Col>
          </Row>
        </div>

        <Table hover className="table-bordered table-hover">
          <thead className="bg-light">
            <tr>
              <th className="border-bottom">UID</th>
              <th className="border-bottom">Name</th>
              <th className="border-bottom">Type</th>
              <th className="border-bottom">Email</th>
              <th className="border-bottom">Status</th>
              <th className="border-bottom">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.map((user, i) => (
              <tr key={i}>
                <td>{user.id}</td>
                <td>{user.name}</td>
                <td>
                  {user.user_type === "1"
                    ? "Admin"
                    : user.user_type === "0"
                    ? "Management"
                    : "Applicant"}
                </td>
                <td>{user.email}</td>
                <td>
                  <span
                    className={`badge ${
                      user.status == 1 ? "bg-success p-2 " : "bg-danger p-2 "
                    }`}
                  >
                    {user.status == 1 ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>
                  {(loggedInUser.user_type === "1" ||
                    loggedInUser.user_type) && (
                    <Dropdown as={ButtonGroup}>
                      <Dropdown.Toggle
                        as={Button}
                        split
                        variant="link"
                        className="text-dark m-0 p-0"
                      >
                        <span className="icon icon-sm">
                          <FontAwesomeIcon
                            icon={faEllipsisH}
                            className="icon-dark"
                          />
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item
                          className="text-warning"
                          onClick={() =>
                            toggleStatus(user.id, user.status == 1 ? 0 : 1)
                          }
                        >
                          <FontAwesomeIcon
                            icon={user.status == 1 ? faToggleOff : faToggleOn}
                            className="me-2"
                          />

                          {user.status == 1 ? "Deactivate" : "Activate"}
                        </Dropdown.Item>

                        {loggedInUser.user_type == 1 && (
                          <Dropdown.Item
                            className="text-danger"
                            onClick={() => deleteUser(user.id)}
                          >
                            <FontAwesomeIcon
                              icon={faTrashAlt}
                              className="me-2"
                            />
                            Remove
                          </Dropdown.Item>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        <Card.Footer className="d-flex justify-content-between align-items-center">
          <Pagination>
            <Pagination.Prev
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Pagination.Prev>
            {Array.from(
              { length: Math.ceil(filteredUsers.length / usersPerPage) },
              (_, index) => (
                <Pagination.Item
                  key={index}
                  active={index + 1 === currentPage}
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </Pagination.Item>
              )
            )}
            <Pagination.Next
              onClick={() =>
                setCurrentPage((prev) =>
                  Math.min(
                    prev + 1,
                    Math.ceil(filteredUsers.length / usersPerPage)
                  )
                )
              }
              disabled={
                currentPage === Math.ceil(filteredUsers.length / usersPerPage)
              }
            >
              Next
            </Pagination.Next>
          </Pagination>
          <small>
            Showing {indexOfFirstUser + 1} to{" "}
            {Math.min(indexOfLastUser, filteredUsers.length)} of{" "}
            {filteredUsers.length} entries
          </small>
        </Card.Footer>
      </Card.Body>
    </Card>
  );
};
